import { DateTime } from 'luxon';
import React, { ChangeEvent, useMemo } from 'react';
import { useState } from 'react';
import { FieldValues, get, Path, set, useFormContext } from 'react-hook-form';
import { FormComponentProps } from '../types';

interface FormDatePickerProps<T> extends FormComponentProps<T> {
  title: string;
  mandatory?: boolean;
  className?: string;
  hideErrorControl?: boolean;
}

const setupDays = (aroundDate: DateTime) => {
  let iterator = DateTime.fromObject({ day: 1, month: aroundDate.month, year: aroundDate.year });
  iterator = iterator.minus({ days: iterator.weekday - 1 });

  let lastDayInDisplay = DateTime.fromObject({
    day: aroundDate.daysInMonth,
    month: aroundDate.month,
    year: aroundDate.year
  });
  lastDayInDisplay = lastDayInDisplay.plus({ days: 7 - lastDayInDisplay.weekday });

  const iteratorNotPastFinalWeek = () => iterator <= lastDayInDisplay;
  const days: DateTime[][] = [];

  while (iteratorNotPastFinalWeek()) {
    const newWeek: DateTime[] = [];
    for (let i = 0; i < 7; ++i) {
      newWeek.push(iterator);
      iterator = iterator.plus({ days: 1 });
    }
    days.push(newWeek);
  }

  return days;
};

export const FormDatePicker = <T extends FieldValues>(props: FormDatePickerProps<T>) => {
  const [displayPicker, setDisplayPicker] = useState(false);
  const {
    formState: { errors },
    setValue,
    getValues,
    setFocus
  } = useFormContext<T>();

  const errorForName = useMemo(() => {
    return get(errors, props.name);
  }, [errors, props.name]);
  const [viewportDate, setViewportDate] = useState(getValues(props.name) ? DateTime.fromISO(getValues(props.name)) : DateTime.now());
  const [selectedDate, setSelectedDate] = useState<DateTime | undefined>(getValues(props.name) ? DateTime.fromISO(getValues(props.name)) : undefined);
  const [days, setDays] = useState(setupDays(selectedDate ?? DateTime.now()));
  const inputRef = React.useRef<HTMLInputElement>(null);

  const viewLastMonth = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const nextDate = viewportDate.minus({ month: 1 });
    setViewportDate(nextDate);
    setDays(setupDays(nextDate));
  };

  const viewNextMonth = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const nextDate = viewportDate.plus({ month: 1 });
    setViewportDate(nextDate);
    setDays(setupDays(nextDate));
  };

  const setViewMonth = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDate = viewportDate.set({ month: parseInt(event.target.value) });
    setViewportDate(nextDate);
    setDays(setupDays(nextDate));
    setFocus(props.name);
  };

  const setViewYear = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextDate = viewportDate.set({ year: parseInt(event.target.value) });
    setViewportDate(nextDate);
    setDays(setupDays(nextDate));
    setFocus(props.name);
  };

  const handleClickDate = (date: DateTime) => {
    setSelectedDate(date);
    setDisplayPicker(false);
    set(errors, props.name, undefined); // remove any errors
    setValue(props.name, date.toUTC(undefined, { keepLocalTime: true }).toISO({ suppressMilliseconds: true }) as T[Path<T>]);
  };

  const handleClickReset = () => {
    setSelectedDate(undefined);
    setValue(props.name, null as T[Path<T>]);
  };

  const onClickInput = () => {
    if (props.readonly === true) return;
    inputRef.current?.focus();
  };

  const textInputOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const validDateRegex = /\d{1,2}\/\d{1,2}\/\d{4}/;
    if (validDateRegex.test(value)) {
      const newDate = DateTime.fromFormat(value, 'd/MM/yyyy');
      if (newDate.isValid) {
        setSelectedDate(newDate);
        setViewportDate(newDate);
        setDays(setupDays(newDate));
        set(errors, props.name, undefined);
        setValue(props.name, newDate.toUTC(undefined, { keepLocalTime: true }).toISO({ suppressMilliseconds: true }) as T[Path<T>]);
        return;
      }
    }
    set(errors, props.name, {
      type: 'DateError',
      message: `Please enter a date as DD/MM/YYYY`
    });
  };

  const headerControl = () => (
    <div>
      <div className="h-[60px] border-elevation-0 border-b-[1px] text-center text-interactive-coral font-semibold text-sm flex flex-row">
        <div className="w-1/5 cursor-pointer h-full flex items-center justify-center" onMouseDown={viewLastMonth}>
          <img src="/datePickerLess.png" className="inline-block datepicker-previous-month" />
        </div>
        <div className={`w-3/5 h-full flex justify-center items-center border-x-[1px] border-elevation-0 datepicker-month-${viewportDate.monthLong} gap-2`} id="datepicker-bg">
          <select value={viewportDate.month ?? ''} className="bg-transparent cursor-pointer" id="datepicker-select" onChange={setViewMonth} tabIndex={-1} onBlur={() => setFocus(props.name)}>
            {Array.from({ length: 12 }, (_, i) => {
              const month = DateTime.fromObject({ month: i + 1 });
              return (
                <option key={i} value={month.month} className="bg-elevation-1">
                  {month.monthLong}
                </option>
              );
            })}
          </select>
          <select value={viewportDate.year ?? ''} className="bg-transparent" id="datepicker-select" onChange={setViewYear} tabIndex={-1} onBlur={() => setFocus(props.name)}>
            {Array.from({ length: 30 }, (_, i) => {
              const year = DateTime.now().year + 15 - i;
              return (
                <option key={i} value={year} className="bg-elevation-1">
                  {year}
                </option>
              );
            })}
          </select>
        </div>
        <div className="w-1/5 cursor-pointer h-full flex items-center justify-center" onMouseDown={viewNextMonth}>
          <img src="/datePickerMore.png" className="inline-block datepicker-next-month" />
        </div>
      </div>
    </div>
  );

  const daysOfTheWeekHeaders = () => (
    <div className="text-xs font-bold text-center pt-6 pl-12 pr-12">
      <div className="inline-block w-1/7">Mon</div>
      <div className="inline-block w-1/7">Tue</div>
      <div className="inline-block w-1/7">Wed</div>
      <div className="inline-block w-1/7">Thu</div>
      <div className="inline-block w-1/7">Fri</div>
      <div className="inline-block w-1/7">Sat</div>
      <div className="inline-block w-1/7">Sun</div>
    </div>
  );
  const datesAreEqual = (l: DateTime, r: DateTime) => l.day === r.day && l.month === r.month && l.year === r.year;

  const dayEntries = () => (
    <div className="text-xs font-bold text-center ml-12 mr-12 pt-4">
      {days.map((week: DateTime[], index: number) => {
        return (
          <div key={`date-picker-week-${index}`} className="text-interactive-coral font-thin">
            {week.map((date: DateTime, index: number) => {
              const isToday = datesAreEqual(date, DateTime.now());
              const isSelectedDate = datesAreEqual(date, selectedDate ?? DateTime.now());
              const className = `cursor-pointer inline-block pt-[2px] w-1/7 h-8 font-thin ${isSelectedDate ? 'bg-solid-bold-green font-black text-elevation-0 datepicker-selected-date' : 'hover:bg-elevation-2'} ${date.month != viewportDate.month ? 'text-secondary-deep-blue-40' : `datepicker-day-${date.day}`}`;
              return (
                <div onMouseDown={() => handleClickDate(date)} key={`date-picker-date-${index}`} className={className}>
                  <div className={`h-7 rounded-3xl m-2 mt-0 border-solid-white pt-[5px] ${isToday && isSelectedDate === false ? 'border-[1px]' : ''}`}>{date.day}</div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );

  const backgroundColour = () => {
    if (props.readonly) {
      return 'bg-transparent';
    } else if (errorForName) {
      return 'bg-feedback-dark-red';
    } else {
      return 'bg-interactive-bold-green-db-12';
    }
  };

  const borderColour = () => {
    if (props.readonly) {
      return 'border-transparent';
    } else if (errorForName) {
      return 'border-b-feedback-red';
    } else {
      return 'border-b-solid-bold-green';
    }
  };

  const labelTextColour = () => {
    if (props.readonly) {
      return 'text-solid-white';
    } else if (errorForName) {
      return 'text-feedback-red';
    } else {
      return 'text-solid-bold-green';
    }
  };

  const errorControl = () => {
    const error = errorForName;
    if (!error) {
      return <br />;
    }
    return <label className="text-feedback-red text-xs">{error.message?.toString()}</label>;
  };

  return (
    <div className={`w-full ${props.className}`}>
      <div
        className={`cursor-text ${backgroundColour()} ${borderColour()} pl-3 border-b-2 pt-2 pb-2 text-solid-white placeholder-shown:text-secondary-deep-blue-30 focus:outline-none`}
        onClick={onClickInput}
      >
        <div className="flex flex-row justify-between pr-3">
          <label className={`cursor-text text-xs ${labelTextColour()} block`}>{props.title}</label>
          {props.mandatory && !props.readonly && <label className={`text-xs ${labelTextColour()} block`}>*</label>}
        </div>
        <input
          ref={inputRef}
          readOnly={props.readonly}
          className={`${backgroundColour()} focus:outline-none w-full`}
          type="text"
          placeholder="DD/MM/YYYY"
          onChange={textInputOnChange}
          value={selectedDate ? selectedDate.toFormat('dd/MM/yyyy') : undefined}
          onBlur={e => {
            if (e.relatedTarget && (e.relatedTarget as HTMLElement).id === 'datepicker-select') {
              return;
            }
            setDisplayPicker(false);
          }}
          onFocus={() => {
            if (props.readonly) return;
            setDisplayPicker(true);
          }}
          tabIndex={props.readonly ? -1 : 0}
        />
      </div>
      <div
        className={`absolute w-max pb-8 z-10 bg-elevation-1 ${displayPicker ? '' : 'hidden'}`}
        id="datepicker-bg"
        onMouseDown={e => {
          if ((e.target as HTMLElement).id === 'datepicker-bg') {
            e.preventDefault();
          }
        }}
      >
        {headerControl()}
        {daysOfTheWeekHeaders()}
        {dayEntries()}
        <div className="flex flex-row justify-center" onMouseDown={e => e.preventDefault()}>
          <p className="underline cursor-pointer" onClick={handleClickReset}>
            Reset
          </p>
        </div>
      </div>
      {!props.hideErrorControl && <div>{errorControl()}</div>}
    </div>
  );
};
